import WorkerAutocompleteBaseController from "./worker_autocomplete_base"

export default class extends WorkerAutocompleteBaseController {
  static identifier = "worker-multi-select"
  static targets = ["input", "dropdown", "selected", "hiddenInputs"]
  static values = { url: String }

  connect() {
    super.connect()
    this.filterSubmitTimeout = null
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    clearTimeout(this.filterSubmitTimeout)
    document.removeEventListener("click", this.handleOutsideClick)
    super.disconnect()
  }

  onInput() {
    this.onAutocompleteInput({ minQueryLength: 2, delay: 200 })
    this.onFilterInput()
  }

  onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 2) this.onAutocompleteInput({ minQueryLength: 2, delay: 0 })
  }

  renderWorkerResults(workers) {
    const dropdown = this.dropdownTarget
    dropdown.replaceChildren()

    const selected = this.selectedIdSet()
    workers.forEach(worker => {
      if (!worker?.id || !worker?.name) return
      const workerId = String(worker.id)
      if (selected.has(workerId)) return

      const item = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "justify-between"
      button.dataset.workerId = workerId
      button.dataset.workerName = worker.name
      button.setAttribute("data-action", "click->worker-multi-select#choose")

      const name = document.createElement("span")
      name.className = "truncate"
      name.textContent = worker.name

      const meta = document.createElement("span")
      meta.className = "text-xs font-mono text-base-content/50"
      meta.textContent = `#${workerId}`

      button.appendChild(name)
      button.appendChild(meta)
      item.appendChild(button)
      dropdown.appendChild(item)
    })

    dropdown.classList.toggle("hidden", dropdown.childElementCount === 0)
  }

  choose(event) {
    const workerId = event.currentTarget?.dataset?.workerId
    const workerName = event.currentTarget?.dataset?.workerName
    if (!workerId || !workerName) return

    if (this.selectedIdSet().has(workerId)) {
      this.inputTarget.value = ""
      this.renderWorkerResults([])
      return
    }

    this.appendWorkerId(workerId)
    this.appendSelectedChip(workerId, workerName)

    this.inputTarget.value = ""
    this.renderWorkerResults([])
    this.submit()
  }

  remove(event) {
    const workerId = event.currentTarget?.dataset?.workerId
    if (!workerId) return

    this.removeWorkerId(workerId)
    this.removeSelectedChip(workerId)
    this.submit()
  }

  onFilterInput() {
    clearTimeout(this.filterSubmitTimeout)
    const query = this.inputTarget.value.trim()

    if (query.length === 0) {
      this.filterSubmitTimeout = setTimeout(() => this.submit(), 200)
      return
    }

    if (query.length < 2) return
    this.filterSubmitTimeout = setTimeout(() => this.submit(), 300)
  }

  handleOutsideClick(event) {
    if (this.element.contains(event.target)) return
    this.renderWorkerResults([])
  }

  submit() {
    const form = this.element.closest("form")
    if (!form) return
    this.replaceUrlFromForm(form)
    form.requestSubmit()
  }

  replaceUrlFromForm(form) {
    if (!window.history?.replaceState) return

    const url = new URL(form.action, window.location.origin)
    const params = new URLSearchParams(new FormData(form))

    if (!params.get("q")) params.delete("q")
    params.delete("authenticity_token")

    url.search = params.toString()
    window.history.replaceState({}, "", url.toString())
    this.updateLeaderboardTabLinks(params)
  }

  updateLeaderboardTabLinks(params) {
    const links = document.querySelectorAll("[data-leaderboard-tab-type]")
    links.forEach(link => {
      const type = link.dataset.leaderboardTabType
      if (!type) return

      const current = new URLSearchParams(params)
      current.set("type", type)

      const linkUrl = new URL(link.getAttribute("href"), window.location.origin)
      const query = current.toString()
      link.setAttribute("href", `${linkUrl.pathname}${query ? `?${query}` : ""}`)
    })
  }

  selectedIdSet() {
    const ids = new Set()
    this.hiddenInputsTarget
      .querySelectorAll('input[name="worker_ids[]"]')
      .forEach(input => {
        if (input?.value) ids.add(String(input.value))
      })
    return ids
  }

  appendWorkerId(workerId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "worker_ids[]"
    input.value = workerId
    this.hiddenInputsTarget.appendChild(input)
  }

  removeWorkerId(workerId) {
    const candidate = Array.from(this.hiddenInputsTarget.querySelectorAll('input[name="worker_ids[]"]')).find(
      input => String(input.value) === String(workerId)
    )
    candidate?.remove()
  }

  appendSelectedChip(workerId, workerName) {
    const chip = document.createElement("span")
    chip.className = "badge badge-outline badge-primary gap-2"
    chip.dataset.workerId = workerId

    const name = document.createElement("span")
    name.className = "truncate max-w-[14rem]"
    name.textContent = workerName

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "btn btn-ghost btn-xs btn-circle"
    remove.dataset.workerId = workerId
    remove.setAttribute("aria-label", `Remove ${workerName}`)
    remove.setAttribute("data-action", "click->worker-multi-select#remove")
    remove.textContent = "×"

    chip.appendChild(name)
    chip.appendChild(remove)
    this.selectedTarget.appendChild(chip)
  }

  removeSelectedChip(workerId) {
    const escaped = window.CSS?.escape ? CSS.escape(String(workerId)) : String(workerId)
    const chip = this.selectedTarget.querySelector(`[data-worker-id="${escaped}"]`)
    chip?.remove()
  }
}
